import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ShieldCheck, AlertCircle, ArrowLeft, Activity, TrendingUp, Layers, UserCheck } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * AdminTrustMetricsPage — Item 10 of Triage Hardening Program (May 27, 2026)
 * Read-only trust + audit dashboard. Passcode-gated (consistent with other admin pages).
 */
export default function AdminTrustMetricsPage() {
  const [unlocked, setUnlocked] = useState(false);
  const [passcode, setPasscode] = useState('');
  const [error, setError] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(false);

  const handleUnlock = (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    axios.get(`${API}/api/admin/trust-metrics?days=${days}&passcode=${encodeURIComponent(passcode.trim())}`)
      .then(r => { setMetrics(r.data); setUnlocked(true); setLoading(false); })
      .catch(err => {
        setLoading(false);
        setError(err?.response?.status === 401 ? 'Incorrect passcode' : (err.message || 'Load failed'));
      });
  };

  useEffect(() => {
    if (!unlocked) return;
    setLoading(true);
    axios.get(`${API}/api/admin/trust-metrics?days=${days}&passcode=${encodeURIComponent(passcode.trim())}`)
      .then(r => { setMetrics(r.data); setLoading(false); })
      .catch(e => { setError(e.message || 'Load failed'); setLoading(false); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unlocked, days]);

  if (!unlocked) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-6">
        <form onSubmit={handleUnlock} className="bg-white rounded-lg shadow-xl p-8 max-w-sm w-full" data-testid="trust-metrics-gate">
          <h1 className="text-lg font-bold text-slate-900 mb-4">Trust & Audit Metrics — passcode required</h1>
          <input
            type="password"
            value={passcode}
            onChange={e => setPasscode(e.target.value)}
            placeholder="passcode"
            className="w-full border-2 border-slate-300 rounded px-3 py-2 mb-3 focus:border-blue-500 outline-none"
            data-testid="trust-metrics-passcode-input"
          />
          {error && <div className="text-red-600 text-xs mb-2">{error}</div>}
          <button type="submit" className="w-full bg-slate-900 text-white py-2 rounded font-semibold hover:bg-slate-700" data-testid="trust-metrics-unlock-btn">Unlock</button>
        </form>
      </div>
    );
  }

  if (loading) return <div className="min-h-screen bg-slate-50 flex items-center justify-center text-slate-600">Loading trust metrics…</div>;
  if (!metrics) return null;

  const fwOk = metrics.health_signals?.deterministic_firewall_intact;
  const counterOk = metrics.health_signals?.encounter_counter_monotonic;

  return (
    <div className="min-h-screen bg-slate-50 pb-12" data-testid="trust-metrics-page">
      <header className="bg-slate-900 text-white border-b-4 border-emerald-500">
        <div className="max-w-7xl mx-auto px-6 py-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Link to="/" className="text-slate-300 hover:text-white text-xs flex items-center gap-1" data-testid="back-to-home-link">
              <ArrowLeft className="w-4 h-4" /> Home
            </Link>
            <div className="w-px h-6 bg-slate-700" />
            <ShieldCheck className="w-5 h-5 text-emerald-400" />
            <div>
              <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-bold">Trust &amp; Audit Metrics · Item 10 · Read-Only</div>
              <div className="text-sm font-semibold">Window: last {metrics.window_days} days · collected {new Date(metrics.collected_at).toLocaleString()}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs text-slate-300">Window:</label>
            <select value={days} onChange={e => setDays(parseInt(e.target.value))} className="bg-slate-800 text-white border border-slate-600 rounded px-2 py-1 text-xs" data-testid="trust-metrics-window-select">
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
            </select>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-6 space-y-6">
        {/* Physicians Actively Validating — registered AND submitted feedback (distinct from "agreed to validate") */}
        <section className="rounded-lg p-5 border-2 bg-white border-teal-400" data-testid="validator-active-section">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <UserCheck className="w-6 h-6 text-teal-600" />
              <div>
                <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wider">Physicians Actively Validating</h2>
                <p className="text-xs text-slate-500 mt-0.5">Registered <em>and</em> submitted structured validation feedback on the platform's deterministic clinical output — distinct from "agreed to validate" (registration only).</p>
              </div>
            </div>
            <div className="text-4xl font-bold text-teal-700 shrink-0" data-testid="validator-active-value">{metrics.validator_active ?? 0}</div>
          </div>
        </section>

        {/* Health Signals */}
        <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className={`rounded-lg p-4 border-2 ${fwOk ? 'bg-emerald-50 border-emerald-400' : 'bg-red-50 border-red-500'}`} data-testid="health-firewall-card">
            <div className="flex items-center gap-2 mb-1">
              {fwOk ? <ShieldCheck className="w-5 h-5 text-emerald-700" /> : <AlertCircle className="w-5 h-5 text-red-700" />}
              <h3 className="font-bold text-sm uppercase tracking-wider">Ambient Firewall</h3>
            </div>
            <div className="text-3xl font-bold">{metrics.totals.firewall_violations_in_window}</div>
            <div className="text-xs text-slate-600">violations in window {fwOk && '(intact)'}</div>
          </div>
          <div className={`rounded-lg p-4 border-2 ${counterOk ? 'bg-emerald-50 border-emerald-400' : 'bg-red-50 border-red-500'}`} data-testid="health-counter-card">
            <div className="flex items-center gap-2 mb-1">
              <Activity className="w-5 h-5 text-emerald-700" />
              <h3 className="font-bold text-sm uppercase tracking-wider">Encounter Counter</h3>
            </div>
            <div className="text-3xl font-bold">{metrics.totals.encounter_counter_current || '—'}</div>
            <div className="text-xs text-slate-600">current monotonic value</div>
          </div>
          <div className="rounded-lg p-4 border-2 bg-blue-50 border-blue-400" data-testid="health-rule-trace-card">
            <div className="flex items-center gap-2 mb-1">
              <Layers className="w-5 h-5 text-blue-700" />
              <h3 className="font-bold text-sm uppercase tracking-wider">Rule Trace / Encounter</h3>
            </div>
            <div className="text-3xl font-bold">{metrics.health_signals?.rule_trace_per_encounter_ratio || '0'}</div>
            <div className="text-xs text-slate-600">avg traces per encounter</div>
          </div>
        </section>

        {/* Triage Distribution */}
        <section className="bg-white rounded-lg border-2 border-slate-200 p-5" data-testid="triage-distribution-section">
          <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-3">Triage Distribution (window)</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[['URGENT', 'bg-red-100 border-red-400 text-red-800'], ['SEMI_URGENT', 'bg-orange-100 border-orange-400 text-orange-800'], ['ROUTINE', 'bg-amber-100 border-amber-400 text-amber-800'], ['PREVENTIVE', 'bg-emerald-100 border-emerald-400 text-emerald-800']].map(([k, cls]) => (
              <div key={k} className={`rounded p-3 border-2 ${cls}`}>
                <div className="text-xs uppercase font-bold tracking-wider">{k.replace('_', '-')}</div>
                <div className="text-2xl font-bold mt-1">{metrics.triage_distribution[k] || 0}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Identity Distribution */}
        <section className="bg-white rounded-lg border-2 border-slate-200 p-5" data-testid="identity-distribution-section">
          <div className="flex items-center gap-2 mb-3">
            <UserCheck className="w-4 h-4 text-slate-600" />
            <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wider">Patient-Identity Resolution (window)</h2>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <h3 className="text-xs font-bold text-slate-600 mb-2">Identity Quality</h3>
              {Object.entries(metrics.identity_quality_distribution || {}).map(([k, v]) => (
                <div key={k} className="flex justify-between text-sm py-1 border-b border-slate-100">
                  <span className="font-semibold">{k}</span>
                  <span>{v}</span>
                </div>
              ))}
            </div>
            <div>
              <h3 className="text-xs font-bold text-slate-600 mb-2">Resolution Outcome</h3>
              {Object.entries(metrics.identity_outcome_distribution || {}).map(([k, v]) => (
                <div key={k} className="flex justify-between text-sm py-1 border-b border-slate-100">
                  <span className="font-semibold">{k.replace('_', ' ')}</span>
                  <span>{v}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Totals */}
        <section className="bg-white rounded-lg border-2 border-slate-200 p-5" data-testid="totals-section">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp className="w-4 h-4 text-slate-600" />
            <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wider">Volumes</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
            {Object.entries(metrics.totals || {}).map(([k, v]) => (
              <div key={k} className="bg-slate-50 rounded p-3 border border-slate-200">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">{k.replace(/_/g, ' ')}</div>
                <div className="text-xl font-bold text-slate-900 mt-1">{v ?? '—'}</div>
              </div>
            ))}
          </div>
        </section>

        <div className="bg-slate-100 border border-slate-300 rounded p-3 text-xs italic text-slate-600">
          {metrics.audit_summary}
        </div>
      </main>
    </div>
  );
}
